import { IServedAd, IServedAdEvent, IServedSliderAd } from 'types';
import { getHeaderOptions, getHttpService } from './http-service';

const httpClient = getHttpService();

const AD_SERVICE_BASE_ROUTE = '/ads';

/**
 * Which partner ads (if any) to show on the waiting screen for this scan.
 * The backend applies the campaign's policy-type targeting, schedule,
 * impression cap, serve percentage and A/B variant — the client just
 * renders what it gets. The scan keys let the backend read the scan's
 * policy type itself; `policyType` is the fallback while the row lands.
 */
export const getActivePartnerAds = async (
  policyType?: string,
  scan?: { policyScanId?: string; policyId?: string },
): Promise<IServedAd[]> => {
  const params: Record<string, string> = {};
  if (policyType) params.policyType = policyType;
  if (scan?.policyScanId) params.policyScanId = scan.policyScanId;
  if (scan?.policyId) params.policyId = scan.policyId;

  const { data } = await httpClient.get(`${AD_SERVICE_BASE_ROUTE}/active`, {
    headers: getHeaderOptions(),
    params: Object.keys(params).length ? params : undefined,
  });
  return Array.isArray(data?.ads) ? data.ads : [];
};

/**
 * The partner slider for the Market Scan results page, or null when no
 * campaign is served (the page then shows CheapestPartnerInfoPopup). Same
 * targeting rules as the waiting-screen ads; at most one slider per scan.
 */
export const getResultsPageSlider = async (
  policyType?: string,
  scan?: { policyScanId?: string; policyId?: string },
): Promise<IServedSliderAd | null> => {
  const params: Record<string, string> = {
    placement: 'results-page-slider',
  };
  if (policyType) params.policyType = policyType;
  if (scan?.policyScanId) params.policyScanId = scan.policyScanId;
  if (scan?.policyId) params.policyId = scan.policyId;

  const { data } = await httpClient.get(`${AD_SERVICE_BASE_ROUTE}/active`, {
    headers: getHeaderOptions(),
    params,
  });
  const ad = Array.isArray(data?.ads) ? data.ads[0] : null;
  return ad && ad.placement === 'results-page-slider' ? ad : null;
};

/**
 * Impression / click for a served ad. Fire-and-forget from the UI.
 *
 * Async on purpose: getHeaderOptions() throws synchronously when there is no
 * ID token, and callers only `.catch()` the promise — a sync throw would
 * escape into the render tree and take the page down with it.
 */
export const recordPartnerAdEvent = async (event: IServedAdEvent) => {
  return httpClient.post(`${AD_SERVICE_BASE_ROUTE}/events`, event, {
    headers: getHeaderOptions(),
  });
};
